// Shared policy — single source of truth for sensitive field tags.
// Imported from BOTH the Azure Functions backend (for server-side
// redaction) AND the React frontend (for the RoleAdmin → Felder tab and
// the <Sensitive> wrapper). If this file ever drifts, the mask leaks.
//
// tag        — short key used by UI + DB (ops.FieldVisibility.field_path)
// label      — admin-facing name (Felder tab)
// paths      — JSON paths to redact in API responses (dot-notation, one or
//              more per tag — e.g. a salary may appear under several keys)
// defaultMode — policy when no explicit RoleFieldVisibility rule exists.
//              `hide` = fail-closed (server omits the key), `blur` = server
//              emits a masked placeholder. Never `show` by default.

/**
 * @typedef {'show'|'blur'|'hide'} FieldMode
 * @typedef {{ tag: string, label: string, paths: string[], defaultMode: FieldMode, category: string }} SensitiveField
 */

/** @type {SensitiveField[]} */
export const SENSITIVE_FIELDS = [
  {
    tag: 'salary',
    label: 'Gehalt (monatlich / jährlich)',
    category: 'Personal',
    // Persona / contract paths commonly emitting salary values.
    paths: [
      'monatsgehalt', 'monatsgehaltBrutto', 'monatsgehaltNetto',
      'jahresgehalt', 'jahresgehaltBrutto',
      'gehalt', 'gehaltBrutto', 'gehaltNetto',
      'vertrag.monatsgehalt', 'vertrag.gehaltBrutto',
    ],
    defaultMode: 'hide',
  },
  {
    tag: 'hourlyRate',
    label: 'Stundensatz',
    category: 'Personal',
    paths: [
      'stundensatz', 'stundenlohn',
      'vertrag.stundensatz', 'vertrag.stundenlohn',
    ],
    defaultMode: 'hide',
  },
  {
    tag: 'bankAccount',
    label: 'Bankverbindung (IBAN / BIC)',
    category: 'Personal',
    paths: [
      'iban', 'bic', 'bankname',
      'bankverbindung', 'bankverbindung.iban', 'bankverbindung.bic',
    ],
    defaultMode: 'hide',
  },
  {
    tag: 'taxId',
    label: 'Steuer-ID / Sozialversicherung',
    category: 'Personal',
    paths: [
      'steuerId', 'steueridentifikationsnummer',
      'sozialversicherungsnummer', 'sozNr',
    ],
    defaultMode: 'hide',
  },
  {
    tag: 'socialSecurity',
    label: 'Krankenversicherung / Mitgliedsnummer',
    category: 'Personal',
    paths: [
      'krankenversicherung', 'krankenkassenNr',
      'mitgliedsnummer', 'versichertennummer',
    ],
    defaultMode: 'blur',
  },
  {
    tag: 'dob',
    label: 'Geburtsdatum',
    category: 'Personal',
    paths: [
      'geburtsdatum', 'geburtstag', 'dob', 'birthday',
    ],
    defaultMode: 'blur',
  },
]

/**
 * Flat lookup — path → tag. Built once at import.
 * @type {Record<string, string>}
 */
export const PATH_TO_TAG = (() => {
  const m = {}
  for (const f of SENSITIVE_FIELDS) for (const p of f.paths) m[p.toLowerCase()] = f.tag
  return m
})()

/**
 * Flat lookup — tag → field record.
 * @type {Record<string, SensitiveField>}
 */
export const TAG_TO_FIELD = Object.fromEntries(SENSITIVE_FIELDS.map(f => [f.tag, f]))

/**
 * Core protected app roles. These cannot be deleted or renamed; their
 * module access is still editable. Keep in sync with the Entra manifest.
 */
export const CORE_ROLES = ['IT.Admin', 'CEO', 'Support']

/** @param {string} role */
export function isCoreRole(role) { return CORE_ROLES.includes(role) }

/**
 * Server-side redaction placeholder.
 * `hide` → undefined (caller deletes the key).
 * `blur` → this string. Client never receives the raw value.
 */
export const BLUR_PLACEHOLDER = '••••'
